import { useCallback, useEffect, useRef, useState } from 'react';
import { getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { toast } from 'react-hot-toast';
import { pushNotificationService } from '@/services/pushNotificationService';
import { userService } from '@/services/userService';

// Guard against missing DefinePlugin keys so we don't access an undefined global `process`
const VAPID_KEY_FALLBACK =
  (typeof process !== 'undefined' && process.env.REACT_APP_FIREBASE_VAPID_KEY) || undefined;
const DEFAULT_TEMPLATE_KEY = 'welcome-notification';
const REGISTERED_TOKEN_KEY = 'push_registered_token';
const MAX_MESSAGE_HISTORY = 10;

const resolveVapidKey = (config) => {
  if (!config) return VAPID_KEY_FALLBACK;
  return (
    config.vapidKey ||
    config.vapidPublicKey ||
    config.webPushVapidKey ||
    config.webPushPublicKey ||
    VAPID_KEY_FALLBACK
  );
};

export const usePushNotifications = (language) => {
  const [firebaseConfig, setFirebaseConfig] = useState(null);
  const [deviceToken, setDeviceToken] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [receivedMessages, setReceivedMessages] = useState([]);

  const firebaseAppRef = useRef(null);
  const messagingRef = useRef(null);
  const onMessageCleanupRef = useRef(null);
  const vapidKeyRef = useRef(resolveVapidKey());
  const didLoadConfigRef = useRef(false);
  const firebaseConfigRef = useRef(null);

  const appendMessage = useCallback((payload, source) => {
    if (!payload) return;

    const timestamp = new Date().toISOString();
    const message = {
      id: `${source}-${timestamp}-${Math.random().toString(36).slice(2, 8)}`,
      source,
      receivedAt: timestamp,
      title: payload.notification?.title || 'Mensagem sem título',
      body: payload.notification?.body || '',
      data: payload.data || null,
      raw: payload
    };

    setReceivedMessages((previous) => {
      const next = [message, ...previous];
      return next.slice(0, MAX_MESSAGE_HISTORY);
    });

    console.info('[push] Mensagem registrada no estado local', { source, title: message.title });
  }, []);

  const propagateConfigToServiceWorker = useCallback(async (config) => {
    if (!('serviceWorker' in navigator) || !config) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      console.info('[push] Service worker pronto, enviando config Firebase');
      registration.active?.postMessage({
        type: 'INIT_FIREBASE',
        config
      });
    } catch (error) {
      console.error('Não foi possível enviar config do Firebase para o service worker:', error);
    }
  }, []);

  const ensureMessaging = useCallback(
    async (configOverride) => {
      const config = configOverride || firebaseConfigRef.current;

      if (!config) {
        throw new Error('Configuração do Firebase não carregada.');
      }

      const supported = await isSupported().catch(() => false);
      if (!supported) {
        throw new Error('Push notifications não são suportadas neste navegador ou protocolo.');
      }

      if (!firebaseAppRef.current) {
        const currentApps = getApps();
        firebaseAppRef.current = currentApps.length ? currentApps[0] : initializeApp(config);
      }

      if (!messagingRef.current) {
        messagingRef.current = getMessaging(firebaseAppRef.current);
      }

      vapidKeyRef.current = resolveVapidKey(config);
      console.info('[push] Config Firebase carregada', { hasVapidKey: Boolean(vapidKeyRef.current) });
      await propagateConfigToServiceWorker(config);

      if (!onMessageCleanupRef.current) {
        onMessageCleanupRef.current = onMessage(messagingRef.current, (payload) => {
          console.info('[push] Payload recebido em foreground', payload);
          appendMessage(payload, 'foreground');
          const title = payload.notification?.title || 'Nova notificação';
          const body = payload.notification?.body || 'Você recebeu um push no ambiente web.';
          toast(
            (t) => (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span><strong>{title}</strong>{body ? ` — ${body}` : ''}</span>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '0 2px' }}
                  aria-label="Fechar notificação"
                >
                  ✕
                </button>
              </span>
            ),
            { duration: Infinity }
          );
        });
      }

      return messagingRef.current;
    },
    [appendMessage, propagateConfigToServiceWorker]
  );

  const loadConfig = useCallback(async () => {
    if (!userService.isAuthenticated()) {
      console.info('[push] loadConfig: usuário não autenticado — não chama endpoint');
      return;
    }
    if (didLoadConfigRef.current) {
      console.info('[push] loadConfig: já carregado (skip)');
      return;
    }

    console.info('[push] loadConfig: iniciando...');
    setIsConfigLoading(true);
    try {
      const config = await pushNotificationService.getPushConfig();
      console.info('[push] loadConfig: API respondeu', config ? { hasConfig: true, hasVapid: Boolean(resolveVapidKey(config)) } : { hasConfig: false });
      if (!config) {
        throw new Error('API retornou config vazia ou null');
      }
      firebaseConfigRef.current = config;
      setFirebaseConfig(config);
      vapidKeyRef.current = resolveVapidKey(config);
      didLoadConfigRef.current = true;
      setConfigError(null);

      console.info('[push] loadConfig: chamando ensureMessaging...');
      await ensureMessaging(config);
      console.info('[push] loadConfig: concluído com sucesso');
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        console.info('[push] loadConfig: push não configurado para este tenant (404)');
        return;
      }
      const message = error.response?.data?.message || error.message || 'Erro ao carregar configuração de push.';
      console.error('[push] loadConfig: erro', message, error);
      setConfigError(message);
    } finally {
      setIsConfigLoading(false);
    }
  }, [ensureMessaging]);

  useEffect(() => {
    if (!userService.isAuthenticated()) return;

    loadConfig();
  }, [loadConfig]);

  // Cleanup do listener onMessage somente no unmount — desacopla do ciclo de vida de loadConfig
  useEffect(() => {
    return () => {
      if (onMessageCleanupRef.current) {
        onMessageCleanupRef.current();
        onMessageCleanupRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;

    const handler = (event) => {
      if (event.data?.type === 'PUSH_RECEIVED') {
        // Foreground: Firebase onMessage já processou esta mensagem — evita toast duplo
        if (document.visibilityState === 'visible') return;
        // Background: SW já exibiu notificação nativa — só atualiza o histórico local
        console.info('[push] Mensagem recebida via service worker (background)', event.data.payload);
        appendMessage(event.data.payload, 'service-worker');
        // Notifica InboxContext para atualizar badge e unread count
        window.dispatchEvent(new CustomEvent('inbox:refresh'));
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handler);
    };
  }, [appendMessage]);

  const requestPermission = useCallback(async () => {
    console.info('[push] requestPermission chamado', {
      hasNotification: typeof Notification !== 'undefined',
      permission: typeof Notification !== 'undefined' ? Notification.permission : 'N/A'
    });
    if (typeof Notification === 'undefined') {
      throw new Error('API de notificações não suportada neste navegador.');
    }

    if (Notification.permission === 'granted') {
      setPermissionStatus('granted');
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      console.warn('[push] Permissão já negada anteriormente; o navegador não exibe o prompt de novo.');
    }

    const permission = await Notification.requestPermission();
    setPermissionStatus(permission);

    if (permission !== 'granted') {
      throw new Error('Permissão de notificação negada pelo usuário.');
    }

    return permission;
  }, []);

  const getServiceWorkerRegistration = async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service worker não disponível.');
    }

    const registration = await navigator.serviceWorker.ready;
    if (!registration) {
      throw new Error('Service worker não está pronto.');
    }

    return registration;
  };

  const generateToken = useCallback(async () => {
    console.info('[push] generateToken: iniciando');
    await loadConfig();
    console.info('[push] generateToken: loadConfig ok, chamando requestPermission (deve exibir prompt)...');
    await requestPermission();
    console.info('[push] generateToken: requestPermission ok');

    console.info('[push] generateToken: ensureMessaging...');
    const messaging = await ensureMessaging();
    console.info('[push] generateToken: getServiceWorkerRegistration...');
    const registration = await getServiceWorkerRegistration();
    const vapidKey = vapidKeyRef.current;

    if (!vapidKey) {
      throw new Error('Chave VAPID não encontrada. Verifique a configuração do tenant.');
    }

    console.info('[push] generateToken: getToken (Firebase)...');
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration
    });

    console.info('[push] Token gerado com sucesso');
    setDeviceToken(token);
    return token;
  }, [ensureMessaging, loadConfig, requestPermission]);

  const registerDevice = useCallback(async () => {
    if (!userService.isAuthenticated()) {
      throw new Error('Usuário deve estar autenticado para registrar dispositivo de push.');
    }
    console.info('[push] registerDevice: iniciando');
    setIsRegistering(true);
    try {
      const token = await generateToken();

      const storedToken = localStorage.getItem(REGISTERED_TOKEN_KEY);
      if (storedToken === token) {
        console.info('[push] registerDevice: token já registrado (skip)');
        return { token, response: null };
      }

      console.info('[push] registerDevice: token obtido, enviando para API...');
      const metadata = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language
      };

      const response = await pushNotificationService.registerDevice({
        token,
        language,
        metadata
      });

      localStorage.setItem(REGISTERED_TOKEN_KEY, token);
      console.info('[push] registerDevice: API respondeu OK');
      toast.success('Dispositivo registrado para push notifications.');
      return { token, response };
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Erro ao registrar dispositivo.';
      console.error('[push] registerDevice: erro', message, error);
      throw error;
    } finally {
      setIsRegistering(false);
    }
  }, [generateToken, language]);

  const sendTestPush = useCallback(
    async (templateKey = DEFAULT_TEMPLATE_KEY, parameters = {}) => {
      if (!userService.isAuthenticated()) {
        throw new Error('Usuário deve estar autenticado para enviar push de teste.');
      }
      setIsSendingTest(true);
      try {
        const response = await pushNotificationService.sendTestPush({
          templateKey,
          parameters
        });
        toast.success('Disparo de teste solicitado.');
        return response;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Erro ao disparar push.';
        console.error('[push] sendTestPush: erro', message, error);
        throw error;
      } finally {
        setIsSendingTest(false);
      }
    },
    []
  );

  return {
    firebaseConfig,
    vapidKey: vapidKeyRef.current,
    deviceToken,
    permissionStatus,
    isConfigLoading,
    isRegistering,
    isSendingTest,
    configError,
    receivedMessages,
    clearReceivedMessages: () => setReceivedMessages([]),
    loadConfig,
    requestPermission,
    generateToken,
    registerDevice,
    sendTestPush
  };
};
